import { createRef, memo, useEffect, useRef, useState } from 'react';
import { BackHandler, Keyboard } from 'react-native';
import { uuid } from '@bonanza-social/toolkit/utils';
import { getBottomSheets } from '../utils';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  BottomSheetConfig,
  BottomSheetManagerExposedProps,
  BottomSheetsChangeListener,
} from './BottomSheetsManager.types';
import CustomBottomSheetModal from '../BottomSheetModal/BottomSheetModal';
import { ErrorBoundaryProvider } from '../hoc';

export let BottomSheet: BottomSheetManagerExposedProps = {
  open: () => {},
  onChange: () => () => {},
};

function BottomSheetsManager() {
  const [sheets, setSheets] = useState<BottomSheetConfig[]>([]);
  const sheetsRef = useRef(sheets);
  const listenersRef = useRef<BottomSheetsChangeListener[]>([]);

  sheetsRef.current = sheets;

  useEffect(() => {
    listenersRef.current.forEach((listener) => listener(sheets));
  }, [sheets]);

  useEffect(() => {
    BottomSheet = {
      onChange: (listener) => {
        listenersRef.current = [...listenersRef.current, listener];
        return () => {
          listenersRef.current = listenersRef.current.filter(
            (l) => l !== listener,
          );
        };
      },
      open: (config) => {
        const {
          namespace,
          queued = false,
          singleton = true,
          replace,
          onClose: userOnClose,
          ...rest
        } = config;
        const type = config.type as Exclude<
          typeof config.type,
          string | number | symbol
        >;
        const components = getBottomSheets();
        const Component = components[namespace][type];
        const ref = createRef<BottomSheetModal>();
        const id = uuid();

        Keyboard.dismiss();

        if (replace) {
          sheetsRef.current.forEach((sheet) => {
            if (sheet.type === type) {
              sheet.onClose();
            }
          });
        }

        setSheets((sheets) => {
          if (sheets.some((sheet) => sheet.type === type) && singleton) {
            return sheets;
          }

          const sheet = {
            ...rest,
            id,
            type,
            Component,
            ref,
            testID: `bottom-sheet-${type}`,
            onClose: () => {
              userOnClose?.();
              ref.current?.dismiss();
            },
            onDismiss: () => {
              setSheets((sheets) => {
                const draft = [...sheets];
                const index = sheets.findIndex((sheet) => sheet.id === id);
                draft.splice(index, 1);
                return draft;
              });
            },
          };

          return queued ? [sheet, ...sheets] : [...sheets, sheet];
        });
      },
    };
  }, []);

  useEffect(() => {
    if (!sheets.length) {
      return;
    }

    const lastSheet = sheets[sheets.length - 1];

    lastSheet.ref.current?.present();
  }, [sheets]);

  useEffect(() => {
    if (sheets.length) {
      const handler = BackHandler.addEventListener('hardwareBackPress', () => {
        const lastSheet = sheets[sheets.length - 1];

        lastSheet.ref.current?.close();

        return true;
      });

      return () => {
        handler.remove();
      };
    }
  }, [sheets]);

  return sheets.map((sheet) => {
    const {
      id,
      type,
      Component,
      data,
      onClose,
      onDismiss,
      contentPaddingHorizontal,
      testID,
      scrollable,
      ...rest
    } = sheet;

    return (
      <CustomBottomSheetModal
        scrollable={scrollable}
        key={id}
        testID={testID}
        close={onClose}
        onDismiss={onDismiss}
        contentPaddingHorizontal={contentPaddingHorizontal}
        {...rest}
      >
        <ErrorBoundaryProvider>
          <Component {...data} close={onClose} />
        </ErrorBoundaryProvider>
      </CustomBottomSheetModal>
    );
  });
}

export default memo(BottomSheetsManager);